import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from "recharts";
import { Box, Typography, useTheme, CircularProgress } from "@mui/material";
import { styled } from "@mui/material/styles";
import { motion } from "framer-motion";
import { AppDispatch, RootState } from "../../stores";
import { getPieChartLeads } from "../../features/lead-module/leadAction";

const StyledChartCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 3),
  borderRadius: 1.5,
  boxShadow: theme.shadows[3],
  backgroundColor: "#fff",
  transition: "all 0.3s ease-in-out",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  "&:hover": {
    transform: "translateY(-2px)",
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
  },
  [theme.breakpoints.down("xs")]: {
    padding: theme.spacing(1.5),
  },
}));

const ChartContainer = styled(Box)(({ theme }) => ({
  height: "320px",
  width: "100%",
  [theme.breakpoints.down("md")]: {
    height: "320px",
  },
  [theme.breakpoints.down("sm")]: {
    height: "290px",
  },
  [theme.breakpoints.down("xs")]: {
    height: "250px",
  },
}));

const NoDataContainer = styled(Box)(({ theme }) => ({
  height: "100%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  backgroundColor: theme.palette.grey[100],
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(2),
  textAlign: "center",
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1.5),
  },
}));

const CustomTooltip = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(1),
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[2],
  border: `1px solid ${theme.palette.divider}`,
  color: theme.palette.text.primary,
  maxWidth: "200px",
  [theme.breakpoints.down("sm")]: {
    maxWidth: "150px",
    padding: theme.spacing(0.75),
  },
}));

// Add the LoaderContainer and LoaderText styled components
const LoaderContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
  width: "100%",
  minHeight: "300px",
}));

const LoaderText = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(2),
  fontSize: "0.9rem",
  color: "#4b5b5d",
  fontWeight: 500,
}));

const PieChartCard = () => {
  const theme = useTheme();
  const dispatch = useDispatch<AppDispatch>();
  const { PieChartLeads, PieChartLeadloading, PieChartLeadError } = useSelector(
    (state: RootState) => state.lead
  );

  useEffect(() => {
    dispatch(getPieChartLeads({ page: 1, pageSize: 100 }));
  }, [dispatch]);

  const statusCounts = PieChartLeads.reduce(
    (acc, lead) => {
      if (lead.status === "new_lead") acc.new_lead += 1;
      else if (lead.status === "tracking") acc.tracking += 1;
      else if (lead.status === "follow_up") acc.follow_up += 1;
      else if (lead.status === "closed") acc.closed += 1;
      return acc;
    },
    { new_lead: 0, tracking: 0, follow_up: 0, closed: 0 }
  );

  const data = [
    { name: "New Lead", value: statusCounts.new_lead, color: "#4b5b5d" },
    { name: "Tracking", value: statusCounts.tracking, color: "#758d8e" },
    { name: "Follow Up", value: statusCounts.follow_up, color: "#f5f0e7" },
    { name: "Closed", value: statusCounts.closed, color: "#d3c8a2" },
  ].filter((entry) => entry.value > 0);

  const CustomTooltipContent = ({
    active,
    payload,
  }: {
    active?: boolean;
    payload?: Array<{
      name?: string;
      value?: number;
      [key: string]: any;
    }>;
  }) => {
    if (active && payload && payload.length) {
      return (
        <CustomTooltip>
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
          >
            {payload[0].name}
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
          >
            {payload[0].value} Leads
          </Typography>
        </CustomTooltip>
      );
    }
    return null;
  };

  const renderCustomizedLabel = (props: any) => {
    const { cx, cy, midAngle, outerRadius, value } = props;
    const RADIAN = Math.PI / 180;
    const sin = Math.sin(-RADIAN * midAngle);
    const cos = Math.cos(-RADIAN * midAngle);
    const mx = cx + (outerRadius + 20) * cos;
    const my = cy + (outerRadius + 20) * sin;
    const textAnchor = cos >= 0 ? "start" : "end";

    return (
      <g>
        <text
          x={mx}
          y={my}
          fill="#4b5b5d"
          textAnchor={textAnchor}
          dominantBaseline="central"
          fontSize={theme.breakpoints.down("sm") ? 12 : 14}
          fontWeight={600}
        >
          {value} Leads
        </text>
      </g>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      style={{ width: "100%", height: "100%" }}
    >
      <StyledChartCard>
        <Typography variant="h6" fontWeight={600} color="#4b5b5d">
          Leads by Status
        </Typography>
        {PieChartLeadloading ? (
          <LoaderContainer>
            <CircularProgress
              size={40}
              thickness={4}
              sx={{
                color: "#4b5b5d",
              }}
            />
            <LoaderText>Loading leads data...</LoaderText>
          </LoaderContainer>
        ) : PieChartLeadError ? (
          <NoDataContainer>
            <Typography
              variant="body1"
              color="error"
              fontWeight={500}
              sx={{ fontSize: { xs: "0.9rem", sm: "1rem" } }}
            >
              {PieChartLeadError}
            </Typography>
          </NoDataContainer>
        ) : !PieChartLeads || data.length === 0 ? (
          <NoDataContainer>
            <Typography
              variant="body1"
              color="text.secondary"
              fontWeight={500}
              sx={{ fontSize: { xs: "0.9rem", sm: "1rem" } }}
            >
              No leads data available
            </Typography>
          </NoDataContainer>
        ) : (
          <ChartContainer>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data}
                  cx="50%"
                  cy="50%"
                  innerRadius={0}
                  outerRadius={theme.breakpoints.down("sm") ? 80 : 100}
                  paddingAngle={3}
                  dataKey="value"
                  labelLine={true}
                  label={renderCustomizedLabel}
                  isAnimationActive={true}
                  animationDuration={800}
                  animationEasing="ease-out"
                >
                  {data.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltipContent />} />
                <Legend
                  layout="horizontal"
                  verticalAlign="bottom"
                  align="center"
                  wrapperStyle={{
                    paddingTop: theme.spacing(
                      theme.breakpoints.down("sm") ? 1 : 2
                    ),
                    fontSize: theme.breakpoints.down("sm")
                      ? "0.75rem"
                      : "0.875rem",
                  }}
                  formatter={(value, entry: any) => (
                    <Typography
                      component="span"
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                    >
                      {`${entry.payload.value} Leads - ${value}`}
                    </Typography>
                  )}
                />
              </PieChart>
            </ResponsiveContainer>
          </ChartContainer>
        )}
      </StyledChartCard>
    </motion.div>
  );
};

export default PieChartCard;
